import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

interface MyIncidentsListProps {
  onClose: () => void;
}

const MyIncidentsList: React.FC<MyIncidentsListProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState<any>(null);

  // Fetch user-specific incidents
  const { data: incidents, isLoading, error } = useQuery(
    ['my-incidents', user?.id],
    () => api.incidents.getUserIncidents(user?.id?.toString() || ''),
    {
      refetchInterval: 30000,
      enabled: !!user?.id, // Only fetch if user ID is available
    }
  );

  const incidentsList = Array.isArray(incidents?.data?.data?.incidents) 
    ? incidents!.data.data.incidents 
    : Array.isArray(incidents?.data?.incidents) 
    ? incidents!.data.incidents 
    : Array.isArray(incidents?.data) 
    ? incidents!.data 
    : [];

  // Filter incidents based on search and filter criteria
  const filteredIncidents = incidentsList.filter((incident: any) => {
    // Fix search filter - if no search term, consider it a match
    const matchesSearch = !searchTerm || 
                         incident.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || incident.status === statusFilter;
    const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
    
    return matchesSearch && matchesStatus && matchesSeverity;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'submitted': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimetype: string) => {
    if (mimetype.startsWith('image/')) {
      return <PhotoIcon className="h-5 w-5 text-blue-600" />;
    } else if (mimetype === 'application/pdf') {
      return <DocumentTextIcon className="h-5 w-5 text-red-600" />;
    } else {
      return <DocumentIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleViewIncident = (incident: any) => {
    setSelectedIncident(incident);
  };

  const handleCloseModal = () => {
    setSelectedIncident(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Incidents</h3>
          <p className="text-gray-600">Unable to load your incident reports. Please try again.</p>
          <button onClick={onClose} className="mt-4 btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Submitted Incidents</h1>
                <p className="mt-1 text-sm text-gray-500">
                  View and track your incident reports
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search incidents..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                className="input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="reported">Reported</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Severity Filter */}
            <div>
              <select
                className="input"
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-center text-sm text-gray-600">
              <FunnelIcon className="h-4 w-4 mr-2" />
              {filteredIncidents.length} of {incidentsList.length} incidents
            </div>
          </div>
        </div>

        {/* Incidents List */}
        {filteredIncidents.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Incidents Found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' || severityFilter !== 'all'
                ? 'No incidents match your current filters.'
                : 'You haven\'t submitted any incident reports yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredIncidents.map((incident: any) => (
              <div key={incident.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {incident.title || 'Untitled Incident'}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(incident.severity)}`}>
                          {incident.severity || 'Unknown'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                          {incident.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {incident.description || 'No description provided'}
                      </p>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{formatDate(incident.created_at)}</span>
                        </div>
                        {incident.location && (
                          <div className="flex items-center space-x-1">
                            <MapPinIcon className="h-4 w-4" />
                            <span>{incident.location}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>ID: {incident.id}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewIncident(incident)}
                        className="btn btn-secondary btn-sm flex items-center space-x-1"
                      >
                        <EyeIcon className="h-4 w-4" />
                        <span>View</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Incident Detail Modal */}
        {selectedIncident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Incident Details
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(selectedIncident.severity)}`}>
                    {selectedIncident.severity || 'Unknown'}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedIncident.status)}`}>
                    {selectedIncident.status?.replace('_', ' ') || 'Unknown'}
                  </span>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-4">
                {/* Basic Information */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    {selectedIncident.title || 'Untitled Incident'}
                  </h4>
                  <p className="text-gray-700 text-base leading-relaxed mb-4">
                    {selectedIncident.description || 'No description provided'}
                  </p>
                  
                  {/* Incident Metadata Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Incident ID:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.incident_id}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Reporter:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.reporter_name}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Category:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.category}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Subcategory:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.subcategory}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Incident Type:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.incident_type}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Submission Method:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.submission_method}</p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="mb-6">
                  <h5 className="text-md font-medium text-gray-900 mb-3">Location Details</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 rounded-lg p-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600">District:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.district || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Constituency:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.constituency || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Ward:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.ward || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Center:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.center || 'N/A'}</p>
                    </div>
                    {selectedIncident.location_lat && selectedIncident.location_lng && (
                      <div className="md:col-span-2">
                        <span className="text-sm font-medium text-gray-600">Coordinates:</span>
                        <p className="text-sm text-gray-900">
                          {selectedIncident.location_lat}, {selectedIncident.location_lng}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Time Information */}
                <div className="mb-6">
                  <h5 className="text-md font-medium text-gray-900 mb-3">Time Information</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-yellow-50 rounded-lg p-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Report Date:</span>
                      <p className="text-sm text-gray-900">{formatDate(selectedIncident.report_date)}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Report Time:</span>
                      <p className="text-sm text-gray-900">{selectedIncident.report_time || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Created:</span>
                      <p className="text-sm text-gray-900">{formatDate(selectedIncident.created_at)}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Last Updated:</span>
                      <p className="text-sm text-gray-900">{formatDate(selectedIncident.updated_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Affected Groups */}
                {selectedIncident.affected_groups && selectedIncident.affected_groups.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-md font-medium text-gray-900 mb-3">Affected Groups</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedIncident.affected_groups.map((group: string, index: number) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                        >
                          {group}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Files */}
                {selectedIncident.evidence_files && selectedIncident.evidence_files.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-md font-medium text-gray-900 mb-3">Evidence Files</h5>
                    <div className="space-y-3">
                      {selectedIncident.evidence_files.map((file: any, index: number) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {getFileIcon(file.mimetype)}
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {file.originalname}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.size)} • Uploaded {formatDate(file.upload_date)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <a
                                href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${file.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                              >
                                <EyeIcon className="h-4 w-4" />
                                <span>View</span>
                              </a>
                              <a
                                href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${file.url}`}
                                download={file.originalname}
                                className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                <span>Download</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyIncidentsList;
